/**
 * CustomerTagListPage.js - 客户标签管理页
 * 支持标签的创建、编辑、删除
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { adminCustomerTagApi } from '../../api/index';

const { Title } = Typography;
const { Option } = Select;

export default function CustomerTagListPage() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // 拉取标签列表
  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminCustomerTagApi.list();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('加载标签列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // 打开新建 Modal
  const handleCreate = () => {
    setEditingTag(null);
    form.setFieldsValue({ name: '', color: '#000000' });
    setModalVisible(true);
  };

  // 打开编辑 Modal
  const handleEdit = (tag) => {
    setEditingTag(tag);
    form.setFieldsValue({
      name: tag.name,
      color: tag.color || '#000000',
    });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingTag(null);
    form.resetFields();
  };

  // 保存标签
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingTag) {
        await adminCustomerTagApi.update(editingTag.id, values);
        message.success('标签已更新');
      } else {
        await adminCustomerTagApi.create(values);
        message.success('标签已创建');
      }
      handleModalClose();
      fetchTags();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除标签
  const handleDelete = async (id) => {
    try {
      await adminCustomerTagApi.delete(id);
      message.success('标签已删除');
      fetchTags();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  const columns = [
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: '颜色预览',
      dataIndex: 'color',
      key: 'color',
      width: 120,
      render: (color) => (
        <Tag color={color || '#000000'} style={{ padding: '4px 12px' }}>
          示例
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val) => val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除该标签？"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          客户标签管理
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建标签
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={tags}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 600 }}
          pagination={false}
          locale={{ emptyText: '暂无标签' }}
        />
      </Card>

      {/* 新建/编辑 Modal */}
      <Modal
        title={editingTag ? '编辑标签' : '新建标签'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        width={480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="name"
            label="标签名称"
            rules={[{ required: true, message: '请输入标签名称' }]}
          >
            <Input placeholder="例：VIP、新客户" />
          </Form.Item>

          <Form.Item
            name="color"
            label="标签颜色"
            rules={[{ required: true, message: '请选择标签颜色' }]}
            initialValue="#gold"
          >
            <Select>
              <Option value="#gold">金色 (VIP)</Option>
              <Option value="#green">绿色 (新客户)</Option>
              <Option value="#blue">蓝色 (潜在客户)</Option>
              <Option value="#red">红色 (高风险)</Option>
              <Option value="#orange">橙色</Option>
              <Option value="#purple">紫色</Option>
              <Option value="#cyan">青色</Option>
              <Option value="#gray">灰色</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
